
import React, { useRef, useEffect } from 'react';
import { analyzePastedImage } from '../services/aiService';

/**
 * Attempts to load an image from a URL, draw it onto a canvas, and extract its base64 representation.
 * This will only succeed if the server hosting the image provides permissive CORS headers.
 * @param {string} imageUrl The URL of the image to load.
 * @returns {Promise<string>} A promise that resolves with the base64 string of the image.
 * @rejects An error if the image fails to load or if the canvas is tainted by CORS policy.
 */
const loadImageAndConvertToBase64 = (imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous'; // Attempt to request the image with CORS headers

        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return reject(new Error('Could not get canvas context.'));
            }
            ctx.drawImage(img, 0, 0);
            
            try {
                // This will throw a security error if the canvas is "tainted" due to a cross-origin image without proper CORS headers.
                const dataUrl = canvas.toDataURL('image/png');
                const base64String = dataUrl.split(',')[1];
                if (!base64String) {
                    return reject(new Error('Failed to extract base64 from data URL.'));
                }
                resolve(base64String);
            } catch (e) {
                reject(new Error(`Canvas was tainted by CORS policy for image: ${imageUrl}`));
            }
        };

        img.onerror = () => {
            reject(new Error(`Failed to load image, likely due to a network or CORS error: ${imageUrl}`));
        };

        img.src = imageUrl;
    });
};


const RichTextInput = ({ label, id, value, onChange, placeholder, disabled, error }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isPasting = useRef(false);

  // Sync external value with editor's content, but not during an async paste operation
  // to avoid overwriting the result of the paste.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value && !isPasting.current) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    // Don't trigger onChange while pasting is in progress to avoid sending partial content.
    if (!isPasting.current) {
      onChange(e.currentTarget.innerHTML);
    }
  };
  
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;

    isPasting.current = true;
    const clipboardData = e.clipboardData;
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
        isPasting.current = false;
        return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();
    
    // Inserts a node and ensures the main component state is updated.
    const insertNodeAndTriggerChange = (node: Node) => {
        range.insertNode(node);
        // Move cursor after the inserted node for better UX.
        selection.collapse(node, node.childNodes.length);
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };
    
    const createAnalysisNode = (description: string): Node => {
        const wrapper = document.createElement('div');
        const formattedDesc = description.replace(/\n/g, '<br>');
        wrapper.innerHTML = `<div class="my-2 p-2 border border-secondary-200 bg-secondary-50 rounded-md" contenteditable="false"><p class="font-semibold text-sm !my-0 text-secondary-700">Image Analysis:</p><p class="!my-1 text-sm text-secondary-900">${formattedDesc}</p></div>`;
        return wrapper.firstElementChild || wrapper;
    };
    
    const createErrorNode = (errorMessage: string): Node => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = `<div class="my-2 p-2 border border-red-200 bg-red-50 rounded-md" contenteditable="false"><p class="font-semibold text-sm !my-0 text-red-700">Image Error:</p><p class="!my-1 text-sm text-red-600">${errorMessage}</p></div>`;
        return wrapper.firstElementChild || wrapper;
    };
    
    const processImageAndReplacePlaceholder = async (base64String: string, placeholderNode: Element) => {
        try {
            const analysis = await analyzePastedImage(base64String);
            const analysisNode = createAnalysisNode(analysis);
            placeholderNode.replaceWith(analysisNode);
        } catch (err: any) {
            const errorNode = createErrorNode(err.message || 'Analysis failed.');
            placeholderNode.replaceWith(errorNode);
        } finally {
            if (editorRef.current) {
                onChange(editorRef.current.innerHTML);
            }
        }
    };
    
    const processFile = (file: File) => {
        const placeholder = document.createElement('div');
        placeholder.className = 'italic text-secondary-500 my-2 p-2 bg-secondary-100 rounded-md';
        placeholder.textContent = `[Analyzing pasted image: ${file.name}...]`;
        placeholder.setAttribute('contenteditable', 'false');
        
        insertNodeAndTriggerChange(placeholder);
        
        const reader = new FileReader();
        reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
            if (typeof loadEvent.target?.result === 'string') {
                const base64String = loadEvent.target.result.split(',')[1];
                processImageAndReplacePlaceholder(base64String, placeholder);
            } else {
                const errorNode = createErrorNode('Could not read image file.');
                placeholder.replaceWith(errorNode);
            }
        };
        reader.onerror = () => {
            const errorNode = createErrorNode('Failed to read image file.');
            placeholder.replaceWith(errorNode);
        };
        reader.readAsDataURL(file);
    };

    // --- Main Paste Logic with priority order ---

    const hasHtml = clipboardData.types.includes('text/html');
    const imageFile = Array.from(clipboardData.files).find(file => file.type.startsWith('image/'));

    // Priority 1: A pasted image file (e.g., from screenshot tool or file system).
    if (imageFile) {
        processFile(imageFile);
    } 
    // Priority 2: Pasted rich text from a webpage or document.
    else if (hasHtml) {
        const html = clipboardData.getData('text/html');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Sanitize the HTML to remove potentially harmful or style-breaking elements.
        tempDiv.querySelectorAll('script, style, link, meta').forEach(el => el.remove());
        
        const images = Array.from(tempDiv.getElementsByTagName('img'));
        
        images.forEach(img => {
            // Handle images embedded as base64 data URIs.
            if (img.src.startsWith('data:image')) {
                const base64String = img.src.split(',')[1];
                const placeholder = document.createElement('div');
                placeholder.className = 'italic text-secondary-500 my-2 p-2 bg-secondary-100 rounded-md';
                placeholder.textContent = '[Analyzing embedded image...]';
                placeholder.setAttribute('contenteditable', 'false');
                img.replaceWith(placeholder);
                // Fire and forget: the analysis will replace the placeholder when done.
                processImageAndReplacePlaceholder(base64String, placeholder);
            } else if (img.src.startsWith('http') || img.src.startsWith('blob')) {
                // Handle remote images by attempting to load them via canvas (respecting CORS).
                const imageUrl = img.src;
                const placeholder = document.createElement('div');
                placeholder.className = 'italic text-secondary-500 my-2 p-2 bg-secondary-100 rounded-md';
                placeholder.textContent = `[Loading image from: ${imageUrl.substring(0, 70)}...]`;
                placeholder.setAttribute('contenteditable', 'false');
                img.replaceWith(placeholder);
                
                // Asynchronously try to load the image via canvas
                loadImageAndConvertToBase64(imageUrl)
                    .then(base64String => {
                        processImageAndReplacePlaceholder(base64String, placeholder);
                    })
                    .catch((err) => {
                        console.warn(`Could not analyze remote image: ${err.message}`);
                        
                        const container = document.createElement('div');
                        container.className = 'remote-image-warning-wrapper my-2 p-2 border-2 border-dashed border-red-500 rounded-md';
                        container.setAttribute('contenteditable', 'false');

                        const originalImgElement = document.createElement('img');
                        originalImgElement.src = imageUrl;
                        originalImgElement.className = "max-w-full h-auto block opacity-50";

                        const explanation = document.createElement('div');
                        explanation.className = "p-2 bg-red-50 text-red-700 text-sm mt-2 rounded";
                        explanation.innerHTML = `<strong>Image Skipped:</strong> Cannot analyze image from a web address due to security policies. Please save the image to your computer and paste the file itself into the editor.`;

                        container.appendChild(originalImgElement);
                        container.appendChild(explanation);
                        
                        placeholder.replaceWith(container);

                        if (editorRef.current) {
                            onChange(editorRef.current.innerHTML);
                        }
                    });
            } else {
                // Unknown image source, remove it or replace with a placeholder.
                const unsupportedNode = createErrorNode(`Unsupported image source: ${img.src.substring(0, 70)}...`);
                img.replaceWith(unsupportedNode);
            }
        });

        const fragment = document.createDocumentFragment();
        while(tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
        }
        insertNodeAndTriggerChange(fragment);
    }
    // Priority 3: Plain text fallback.
    else {
        const plainText = clipboardData.getData('text/plain');
        range.insertNode(document.createTextNode(plainText));
    }

    // Defer resetting the flag to allow async operations to start and update the DOM.
    setTimeout(() => {
        isPasting.current = false;
        // Final state update after all synchronous DOM insertions are complete.
        if(editorRef.current) {
           onChange(editorRef.current.innerHTML);
        }
    }, 100);
  };
  

  const ringColor = error ? 'ring-red-500' : 'ring-secondary-300';
  const focusRingColor = error ? 'focus:ring-red-600' : 'focus:ring-primary-600';
  const hasError = !!error;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium leading-6 text-secondary-900">
        {label}
      </label>
      <div className="mt-2">
        <div
          id={id}
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onPaste={handlePaste}
          className={`prose max-w-none block w-full rounded-md border-0 py-1.5 px-2.5 text-secondary-900 shadow-sm ring-1 ring-inset ${ringColor} placeholder:text-secondary-400 focus:ring-2 focus:ring-inset ${focusRingColor} sm:text-sm sm:leading-6 min-h-[150px] ${disabled ? 'bg-secondary-100 cursor-not-allowed' : 'bg-white'}`}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        ></div>
        {error && <p className="mt-2 text-sm text-red-600" id={`${id}-error`}>{error}</p>}
      </div>
    </div>
  );
};

export default RichTextInput;
